import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity, FileText, Key, Box, Server, Folder,
  Plus, Edit2, Filter, ChevronDown, RefreshCw
} from 'lucide-react';
import { documentationAPI } from '../../services/api';

// Entity type configuration
const entityConfig = {
  article: {
    icon: FileText,
    label: 'Article',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    linkPrefix: '/documentation/articles/',
  },
  password: {
    icon: Key,
    label: 'Password',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    linkPrefix: '/vault/passwords/',
  },
  flexible_asset: {
    icon: Box,
    label: 'Asset',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    linkPrefix: '/documentation/assets/',
  },
  configuration: {
    icon: Server,
    label: 'Configuration',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    linkPrefix: '/documentation/configurations/',
  },
  folder: {
    icon: Folder,
    label: 'Folder',
    color: 'text-neutral-600 dark:text-neutral-400',
    bgColor: 'bg-neutral-100 dark:bg-neutral-700',
    linkPrefix: null, // Folders don't have detail pages
  },
};

// Action configuration
const actionConfig = {
  created: {
    icon: Plus,
    label: 'Created',
    color: 'text-green-600 dark:text-green-400',
  },
  updated: {
    icon: Edit2,
    label: 'Updated',
    color: 'text-blue-600 dark:text-blue-400',
  },
};

// Format relative time
function formatRelativeTime(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString();
}

// Get date group label
function getDateGroup(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);
  const diffDays = Math.floor((now - date) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'This Week';
  if (diffDays < 30) return 'This Month';
  return 'Earlier';
}

// Activity item component
function ActivityItem({ item }) {
  const entityCfg = entityConfig[item.entity_type] || entityConfig.folder;
  const actionCfg = actionConfig[item.action] || actionConfig.updated;
  const EntityIcon = entityCfg.icon;
  const ActionIcon = actionCfg.icon;

  const userName = item.user_first_name && item.user_last_name
    ? `${item.user_first_name} ${item.user_last_name}`
    : item.user_email || 'Unknown user';

  return (
    <div className="flex gap-4 p-4 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
      {/* Entity Icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${entityCfg.bgColor}`}>
        <EntityIcon className={`w-5 h-5 ${entityCfg.color}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Action Badge */}
          <span className={`inline-flex items-center gap-1 text-xs font-medium ${actionCfg.color}`}>
            <ActionIcon className="w-3 h-3" />
            {actionCfg.label}
          </span>

          {/* Entity Type Badge */}
          <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 rounded">
            {entityCfg.label}
          </span>
        </div>

        {/* Entity Name */}
        <div className="mt-1">
          {entityCfg.linkPrefix ? (
            <Link
              to={`${entityCfg.linkPrefix}${item.entity_id}`}
              className="font-medium text-neutral-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              {item.entity_name}
            </Link>
          ) : (
            <span className="font-medium text-neutral-900 dark:text-white">
              {item.entity_name}
            </span>
          )}
        </div>

        {/* User and Time */}
        <div className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          by {userName}
        </div>
      </div>

      {/* Timestamp */}
      <div className="flex-shrink-0 text-sm text-neutral-500 dark:text-neutral-400">
        {formatRelativeTime(item.timestamp)}
      </div>
    </div>
  );
}

export default function ActivityFeedPage() {
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Fetch activity
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['documentation', 'activity', entityTypeFilter],
    queryFn: () => documentationAPI.getActivity({
      limit: 100,
      entityType: entityTypeFilter || undefined,
    }),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const activity = data?.data?.activity || [];

  // Group activity by date
  const groupedActivity = useMemo(() => {
    const groups = {};
    activity.forEach(item => {
      const group = getDateGroup(item.timestamp);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
    });
    return groups;
  }, [activity]);

  const groupOrder = ['Today', 'Yesterday', 'This Week', 'This Month', 'Earlier'];

  const entityTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'article', label: 'Articles' },
    { value: 'password', label: 'Passwords' },
    { value: 'flexible_asset', label: 'Assets' },
    { value: 'configuration', label: 'Configurations' },
    { value: 'folder', label: 'Folders' },
  ];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 dark:bg-brand-900/30 rounded-lg">
            <Activity className="w-6 h-6 text-brand-600 dark:text-brand-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              Activity Feed
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Recent changes across all documentation
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>

          {/* Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
            >
              <Filter className="w-4 h-4 text-neutral-500" />
              <span className="text-neutral-700 dark:text-neutral-300">
                {entityTypeOptions.find(o => o.value === entityTypeFilter)?.label || 'All Types'}
              </span>
              <ChevronDown className="w-4 h-4 text-neutral-500" />
            </button>

            {showFilterDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowFilterDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-20">
                  {entityTypeOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setEntityTypeFilter(option.value);
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        entityTypeFilter === option.value
                          ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                          : 'text-neutral-700 dark:text-neutral-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading activity...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600 dark:text-red-400">Failed to load activity feed</p>
            <button
              onClick={() => refetch()}
              className="mt-2 text-brand-600 dark:text-brand-400 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : activity.length === 0 ? (
          <div className="p-8 text-center">
            <Activity className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">No activity yet</p>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
              Changes to documentation will appear here
            </p>
          </div>
        ) : (
          <div>
            {groupOrder.map(group => {
              const items = groupedActivity[group];
              if (!items || items.length === 0) return null;

              return (
                <div key={group}>
                  {/* Date Group Header */}
                  <div className="px-4 py-2 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
                    <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                      {group}
                    </h3>
                  </div>

                  {/* Activity Items */}
                  <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                    {items.map((item, index) => (
                      <ActivityItem key={`${item.entity_type}-${item.entity_id}-${index}`} item={item} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Load More */}
      {data?.data?.hasMore && (
        <div className="mt-4 text-center">
          <button className="text-brand-600 dark:text-brand-400 hover:underline">
            Load more activity
          </button>
        </div>
      )}
    </div>
  );
}
